import { LogEvents } from '@/types';
import { useFetchAxios, useGlobals } from '@/hooks';
import { getOnStorage, setOnStorage } from '@/shared';

interface LogEventsPartial extends Omit<LogEvents, 'id' | 'origemEvento' | 'dtEvento' | 'origemDispositivo' | 'rotaPagina' | 'codRca' | 'codLoja' | 'codCliente' | 'idCarrinho' | 'tipoUsuario' | 'stEvento' | 'codProduto'> {
  tipoUsuario?: string;
  codRca?: number;
  codProduto?: number;
  codLoja?: number;
  codCliente?: number;
  idCarrinho?: number;
  stEvento?: number;
}

const getPlatform = (): string => {
  const isMobile =
    navigator.userAgentData?.mobile ?? /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const deviceType = isMobile ? 'MOBILE' : 'DESKTOP';
  return deviceType;
};

export const collectPWAMetrics = async () => {
  const nav = navigator as any;
  const storage = await nav.storage.estimate();
  const performance = window.performance as any;

  return {
    device: {
      // RAM do aparelho em GB (navigator.deviceMemory). Pode ser undefined em alguns browsers; nesse caso vira null.
      memoryGB: nav.deviceMemory || null,
      // Número de núcleos lógicos da CPU (navigator.hardwareConcurrency). Também pode ser undefined e virar null.
      cpuCores: nav.hardwareConcurrency || null,
    },
    storage: storage && {
      // Espaço total disponível para armazenamento da origem, em MB (storage.quota convertido de bytes).
      quotaMB: Math.round(storage.quota / 1024 / 1024),
    },
    network: nav.connection && {
      // Tipo efetivo de conexão (effectiveType): ex. "4g", "3g", "slow-2g".
      type: nav.connection.effectiveType,
    },
    runtime: {
      // Memória de heap JS usada no momento, em MB (performance.memory.usedJSHeapSize). Só em Chrome/Chromium; em outros browsers fica null.
      heapMB: performance.memory ? Math.round(performance.memory.usedJSHeapSize / 1024 / 1024) : null,
      // Tempo em ms desde o início do carregamento da página (performance.now()). Útil para medir duração de operações.
      time: performance.now(),
    },
    environment: {
      // String completa do User-Agent do navegador.
      userAgent: nav.userAgent,
      // Plataforma do SO reportada pelo browser (navigator.platform), ex. "MacIntel", "Win32".
      platform: nav.platform,
      // Se o app está rodando como PWA instalado (janela sem barra do browser). true quando (display-mode: standalone) dá match; false quando está na aba do browser.
      standalone: window.matchMedia('(display-mode: standalone)').matches,
    },
  };
};

export const useEvents = () => {
  const { version } = useGlobals();
  const { axiosCtx } = useFetchAxios();

  const registerEvent = async (logEvents: LogEventsPartial) => {
    return;
    const orderNumbers = getOnStorage('order_numbers');
    const offlineEvents = getOnStorage('offline_events');
    const selectedClient = getOnStorage('selected_client');

    const event: Partial<Omit<LogEvents, 'id'>> = {
      tipoEvento: logEvents.tipoEvento || 'UNKNOWN',
      obsEvento: logEvents.obsEvento || 'UNKNOWN',

      origemEvento: 'PWA',
      dtEvento: new Date().toISOString(),
      origemDispositivo: getPlatform(),
      rotaPagina: window.location.hash.replace('#', ''),

      codProduto: logEvents.codProduto || 0,
      codRca: Number(logEvents.codRca || getOnStorage('cod_pessoa') || 0),
      codLoja: Number(logEvents.codLoja || selectedClient?.COD_LOJA || 0),
      codCliente: Number(logEvents.codCliente || selectedClient?.COD_CLIENTE || 0),
      idCarrinho: Number(logEvents.idCarrinho || orderNumbers?.[selectedClient?.COD_CLIENTE || '']?.orderNumber) || null,
      tipoUsuario: logEvents.tipoUsuario || getOnStorage('rca_usuario')?.tipoUsuario || 'RCA',
      
      // 1: Sucesso, 0: Falha
      stEvento: logEvents.stEvento || 1,
    };

    const body = [...(offlineEvents || []), event].map((event, index) => ({ ...event, id: index + 1 }));

    if (navigator.onLine) {
      return axiosCtx.post('/api-proxy', {
        body,
        method: 'POST',
        endpoint: '/v1/Insere-eventos',
      });
    } else {
      setOnStorage('offline_events', body);
      return Promise.resolve(true);
    }
  };

  const registerVersionEvent = async (codRca?: number) => {
    const currentVersion = getOnStorage('system_version');
    if (currentVersion === version) return;

    setOnStorage('system_version', version);
    registerEvent({
      tipoEvento: 'VERSAO',
      obsEvento: `Versão do PWA: ${version} | RCA: ${codRca}`,
      codRca: Number(codRca || getOnStorage('cod_pessoa') || 0),
    })
  }

  return { registerEvent, registerVersionEvent };
};
